import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

// YouTube API type declarations
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Trash2, MapPin, Briefcase, DollarSign, Smartphone, Loader2, XCircle, HelpCircle, RotateCcw, BarChart3, ClipboardList, Users, Star, Heart } from "lucide-react";
import { RatingsModal } from './RatingsModal';
import { useToast } from "@/hooks/use-toast";
import { PersonaDoc, searchPersonas, getAllPersonasCached } from "@/dao/firestore/PersonasRepository";
import { uploadBytes, ref, getDownloadURL } from 'firebase/storage';
import { storage, db } from '@/integrations/firebase/client';
import { doc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import FeedbackList from '@/components/studio/FeedbackList';
import { updateFeedback, type FeedbackDoc } from '@/dao/firestore/FeedbacksRepository';
import { listFeedbackResults, type FeedbackResultDoc } from '@/dao/firestore/FeedbackResultsRepository';
import { PersonalFeedbackResult } from './PersonalFeedbackResult';
import { ScreeningRoomEventModal } from './ScreeningRoomEventModal';
import { PersonalFeedbackModal } from './PersonalFeedbackModal';
import { ResultsAnalyticsModal } from './ResultsAnalyticsModal';
import RecommendationsModal from './RecommendationsModal';
import { AudienceModal } from './AudienceModal';
import { AnalyticsModal } from './AnalyticsModal';
import { getFeedbackSummary, FeedbackSummaryDoc } from '@/dao/firestore/FeedbackSummaryRepository';
import { validatePersonaResult, validateSummary, logValidationErrors } from '@/lib/schema-validator';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import InsightsList from './InsightsList';
import type { VideoSource } from '@/dao/types';
import { calculateCredits, type ServiceType } from '@/lib/credits';
import { useTranslation } from 'react-i18next';
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
export type FeedbackStudioProps = {
  uid: string | null;
};
function isYouTubeUrl(url: string) {
  return /(youtube\.com\/watch\?v=|youtu\.be\/)/i.test(url);
}
function looksLikeVideoUrl(url: string) {
  return /^(https?:)\/\/.+\.(mp4|mov|webm|ogg)(\?.*)?$/i.test(url);
}
export default function FeedbackStudio({
  uid
}: FeedbackStudioProps) {
  const {
    toast
  } = useToast();
  const {
    t
  } = useTranslation();
  const [selectedVideo, setSelectedVideo] = useState<VideoSource | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState('');
  const [personas, setPersonas] = useState<PersonaDoc[]>([]);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [ageRange, setAgeRange] = useState<[number, number]>([0, 100]);
  const [searchText, setSearchText] = useState('');
  const [filterTag, setFilterTag] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [previewPersona, setPreviewPersona] = useState<PersonaDoc | null>(null);
  const [selectedResult, setSelectedResult] = useState<FeedbackResultDoc | null>(null);
  const [personalFeedbackModalOpen, setPersonalFeedbackModalOpen] = useState(false);
  const [screeningRoomModalOpen, setScreeningRoomModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackDoc | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [results, setResults] = useState<FeedbackResultDoc[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [secondEditorOpinion, setSecondEditorOpinion] = useState<boolean>(false);
  const [secondEditorInfoOpen, setSecondEditorInfoOpen] = useState(false);
  const [audienceCatalogHelpOpen, setAudienceCatalogHelpOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const [resultsAnalyticsOpen, setResultsAnalyticsOpen] = useState(false);
  const [recommendationsOpen, setRecommendationsOpen] = useState(false);
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [ratingsModalOpen, setRatingsModalOpen] = useState(false);
  const [summaryData, setSummaryData] = useState<FeedbackSummaryDoc | null>(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState<number>(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Compute moments from persona results
  const videoMoments = useMemo(() => {
    const moments: { timeMs: number; type: 'best' | 'worst'; personaName: string; comment: string }[] = [];
    const personalResults = results.filter(r => r.agent === 'personal-feedback');
    for (const r of personalResults) {
      if (r.better_moment?.millisecond) {
        moments.push({ timeMs: r.better_moment.millisecond, type: 'best', personaName: r.personaName || '', comment: r.better_moment.comment || '' });
      }
      if (r.worst_moment?.millisecond) {
        moments.push({ timeMs: r.worst_moment.millisecond, type: 'worst', personaName: r.personaName || '', comment: r.worst_moment.comment || '' });
      }
    }
    moments.sort((a, b) => a.timeMs - b.timeMs);
    return moments;
  }, [results]);

  const seekTo = (timeMs: number) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = timeMs / 1000;
      video.play();
    }
  };

  useEffect(() => {
    if (!uid) return;
    const ref = doc(db, 'users', uid, 'settings', 'credits');
    const unsub = onSnapshot(ref, snap => {
      const total = snap.data()?.total;
      setCredits(typeof total === 'number' ? total : 0);
    });
    return unsub;
  }, [uid]);
  const runningGifSrc = useMemo(() => {
    const idx = Math.floor(Math.random() * 10);
    return `/${idx}.gif`;
  }, [selectedFeedback?.id]);
  useEffect(() => {
    if (!selectedFeedback) {
      // Clear all data when no feedback is selected
      setSelectedVideo(null);
      setAgeRange([0, 100]);
      setSearchText('');
      setSelectedIds({});
      setSelectedOrderIds([]);
      setNotes('');
      setSecondEditorOpinion(false);
      setResults([]);
      return;
    }

    // Update all states when feedback changes
    setSelectedVideo(selectedFeedback.video ?? null);
    setAgeRange((selectedFeedback as any).ageRange ?? (selectedFeedback as any)?.filters?.ageRange ?? [0, 100]);
    setSearchText((selectedFeedback as any)?.filters?.searchText ?? (selectedFeedback as any)?.searchText ?? '');
    const sel = (selectedFeedback as any).selectedPersonas ?? [];
    const map = sel.reduce((acc: Record<string, boolean>, p: any) => {
      acc[p.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setSelectedIds(map);
    setSelectedOrderIds(sel.map((p: any) => p.id));
    setNotes((selectedFeedback as any).notes ?? '');
    setSecondEditorOpinion((selectedFeedback as any).secondEditorOpinion ?? false);
  }, [selectedFeedback?.id]); // Only depend on ID to avoid unnecessary updates
  const loadPersonas = useCallback(async () => {
    if (!uid) return;
    setLoadingPersonas(true);
    try {
      const items = await getAllPersonasCached();
      const sorted = [...items].sort((a, b) => {
        const ao = a.order;
        const bo = b.order;
        if (typeof bo === 'number' && typeof ao === 'number') {
          if (bo !== ao) return bo - ao;
        } else if (typeof bo === 'number') {
          return -1;
        } else if (typeof ao === 'number') {
          return 1;
        }
        return a.id.localeCompare(b.id);
      });
      setPersonas(sorted);
    } catch (error) {
      console.error('Error loading personas:', error);
      toast({
        title: t('studio.critics.error'),
        description: t('studio.critics.loadError'),
        variant: "destructive"
      });
    } finally {
      setLoadingPersonas(false);
    }
  }, [uid, toast]);
  const refreshCatalog = useCallback(async () => {
    if (!uid) return;
    setLoadingPersonas(true);
    try {
      const items = await getAllPersonasCached({
        force: true
      });
      const sorted = [...items].sort((a, b) => {
        const ao = a.order;
        const bo = b.order;
        if (typeof bo === 'number' && typeof ao === 'number') {
          if (bo !== ao) return bo - ao;
        } else if (typeof bo === 'number') {
          return -1;
        } else if (typeof ao === 'number') {
          return 1;
        }
        return a.id.localeCompare(b.id);
      });
      setPersonas(sorted);
    } catch (error) {
      console.error('Error refreshing personas:', error);
      toast({
        title: t('studio.critics.error'),
        description: t('studio.critics.loadError'),
        variant: "destructive"
      });
    } finally {
      setLoadingPersonas(false);
    }
  }, [uid, toast]);
  const filterTags = useMemo(() => {
    const set = new Set<string>();
    personas.forEach(p => {
      const tag = (p as any).filter_tag;
      if (typeof tag === 'string' && tag.trim()) set.add(tag.trim());
    });
    return Array.from(set).sort();
  }, [personas]);
  const filteredPersonas = useMemo(() => {
    const filtered = personas.filter(p => {
      // Age filter - only apply if range is not the full range (0-100)
      if (ageRange[0] > 0 || ageRange[1] < 100) {
        if (p.age == null || p.age < ageRange[0] || p.age > ageRange[1]) return false;
      }

      // Filter tag
      if (filterTag !== 'all') {
        const tag = (p as any).filter_tag;
        if (!tag || tag !== filterTag) return false;
      }

      // Text search filter - match across all persona fields (full JSON)
      if (searchText.trim()) {
        const tokens = searchText.toLowerCase().split(/[\s,]+/).map(t => t.trim()).filter(Boolean);
        const haystack = JSON.stringify(p).toLowerCase();
        if (!tokens.every(tk => haystack.includes(tk))) return false;
      }
      return true;
    });

    // Sort by 'order' desc; undefined at the end; tie-breaker by id
    return filtered.sort((a, b) => {
      const ao = a.order;
      const bo = b.order;
      if (typeof bo === 'number' && typeof ao === 'number') {
        if (bo !== ao) return bo - ao;
      } else if (typeof bo === 'number') {
        return -1;
      } else if (typeof ao === 'number') {
        return 1;
      }
      return a.id.localeCompare(b.id);
    });
  }, [personas, ageRange, searchText, filterTag]);
  useEffect(() => {
    loadPersonas();
  }, [loadPersonas]);
  const selectedList = useMemo(() => {
    return personas.filter(p => selectedIds[p.id]);
  }, [personas, selectedIds]);
  const services: ServiceType[] = secondEditorOpinion ? ['proAnalysis'] : [];
  const creditCalculation = calculateCredits(
    (selectedVideo?.duration || 0) / 1000, // Convert from milliseconds to seconds
    selectedList.length,
    services
  );
  const totalCost = creditCalculation.total;
  const canRequest = !!selectedVideo && selectedList && selectedList.length > 0;
  const insufficientCredits = (credits ?? 0) < totalCost;
  const selectedOrdered = useMemo(() => {
    const order = selectedOrderIds.filter(id => selectedIds[id]);
    const map = new Map(personas.map(p => [p.id, p]));
    const selectedIdsList = Object.keys(selectedIds).filter(id => selectedIds[id]);
    const newly = selectedIdsList.filter(id => !order.includes(id));
    const orderedIds = [...newly, ...order];
    const arr = orderedIds.map(id => map.get(id)).filter(Boolean) as PersonaDoc[];
    return arr.length ? arr : personas.filter(p => selectedIds[p.id]);
  }, [selectedOrderIds, selectedIds, personas]);
  useEffect(() => {
    if (!selectedFeedback?.id) {
      setResults([]);
      setLoadingResults(false);
      return;
    }
    if ((selectedFeedback as any).status !== 'done') {
      setResults([]);
      setLoadingResults(false);
      return;
    }
    setLoadingResults(true);
    console.log('[FEEDBACK] loading results for', selectedFeedback.id, 'status=', (selectedFeedback as any).status);
    listFeedbackResults(selectedFeedback.id).then(res => {
      console.log('[FEEDBACK] results loaded:', res.length, 'items');
      console.log('[FEEDBACK] raw result keys:', res.map(r => Object.keys(r)));
      res.forEach(r => {
        console.log(`[FEEDBACK] result ${r.id}:`, {
          agent: r.agent,
          personaId: r.personaId,
          hasComment: !!r.comment,
          hasRating: typeof r.rating,
          hasVisual: r.visual ? `{rating: ${r.visual?.rating}}` : 'undefined',
          hasContent: r.content ? `{rating: ${r.content?.rating}}` : 'undefined',
          visualKeys: r.visual ? Object.keys(r.visual) : [],
          contentKeys: r.content ? Object.keys(r.content) : [],
          hasInsights: Array.isArray(r.insights),
          errorType: r.error ? typeof r.error : 'no-error',
          errorValue: r.error ? JSON.stringify(r.error) : null,
        });
        // Validate each persona result against schema
        if (r.agent === 'personal-feedback') {
          const errors = validatePersonaResult(r);
          logValidationErrors(`persona ${r.personaId || r.id}`, errors);
        }
      });
      setResults(res);
    }).catch(error => {
      console.error('[FEEDBACK] Error loading results:', error);
      setResults([]);
    }).finally(() => setLoadingResults(false));
  }, [selectedFeedback?.id, selectedFeedback?.status]);

  // Load summary data when feedback changes
  useEffect(() => {
    if (!selectedFeedback?.id || selectedFeedback?.status !== 'done') {
      setSummaryData(null);
      return;
    }
    getFeedbackSummary(selectedFeedback.id).then(data => {
      if (data) {
        console.log('[FEEDBACK] summary loaded:', {
          hasInsights: Array.isArray(data.insights),
          insightsCount: data.insights?.length,
          hasSenseAnalysis: Array.isArray(data.sense_analysis),
          senseAnalysisCount: data.sense_analysis?.length,
          hasRatings: Array.isArray(data.ratings),
          ratingsCount: data.ratings?.length,
          ratingsSample: data.ratings?.[0] ? JSON.stringify(data.ratings[0]) : null,
          recommendationsCount: data.recommendations?.length,
        });
        const errors = validateSummary(data);
        logValidationErrors('summary', errors);
      } else {
        console.log('[FEEDBACK] summary is null/undefined');
      }
      setSummaryData(data);
    });
  }, [selectedFeedback?.id, selectedFeedback?.status]);

  // Helper function to get video duration
  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(Math.round(video.duration * 1000)); // Convert to milliseconds
      };
      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Error loading video metadata'));
      };
      video.src = URL.createObjectURL(file);
    });
  };
  const onFile = useCallback(async (file: File) => {
    const path = `user-videos/${uid ?? 'anon'}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    setUploading(true);
    try {
      // Get video duration before upload
      const duration = await getVideoDuration(file);

      if (duration > 600000) { // 10 minutes in milliseconds
        toast({
          title: t('studio.critics.videoTooLongErrorTitle'),
          description: t('studio.critics.videoTooLongErrorDesc'),
          variant: "destructive"
        });
        setUploading(false);
        return;
      }

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      const vid: VideoSource = {
        type: 'upload',
        storagePath: path,
        downloadURL,
        duration
      };
      setSelectedVideo(vid);
      if (selectedFeedback?.id) {
        await updateFeedback(selectedFeedback.id, {
          video: vid
        } as any);
      }
      toast({
        title: t('studio.critics.videoUploaded'),
        description: t('studio.critics.videoUploadedDesc')
      });
    } catch (e) {
      toast({
        title: t('studio.critics.error'),
        description: String(e),
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  }, [uid, selectedFeedback?.id, toast]);
  // Helper function to get video duration from URL
  const getVideoDurationFromUrl = (videoUrl: string): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.crossOrigin = 'anonymous';
      video.onloadedmetadata = () => {
        resolve(Math.round(video.duration * 1000)); // Convert to milliseconds
      };
      video.onerror = () => {
        // If we can't get duration, resolve with 0 instead of rejecting
        resolve(0);
      };
      video.src = videoUrl;
    });
  };

  // Helper function to get YouTube video duration using IFrame Player API
  const getYouTubeDuration = async (videoUrl: string): Promise<number> => {
    return new Promise(resolve => {
      const videoId = videoUrl.split('v=')[1]?.split('&')[0] || videoUrl.replace('https://youtu.be/', '');

      // Create a temporary iframe to get duration
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.top = '-9999px';
      tempContainer.style.left = '-9999px';
      tempContainer.id = `temp-player-${Date.now()}`;
      document.body.appendChild(tempContainer);

      // Load YouTube IFrame API if not already loaded
      if (!window.YT) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(script);
        window.onYouTubeIframeAPIReady = () => {
          createPlayer();
        };
      } else {
        createPlayer();
      }
      function createPlayer() {
        const player = new window.YT.Player(tempContainer.id, {
          height: '1',
          width: '1',
          videoId: videoId,
          events: {
            onReady: (event: any) => {
              try {
                const duration = event.target.getDuration();
                const durationMs = Math.round(duration * 1000);
                resolve(durationMs);
              } catch (error) {
                console.error('Error getting YouTube duration:', error);
                resolve(0);
              } finally {
                // Cleanup
                event.target.destroy();
                document.body.removeChild(tempContainer);
              }
            },
            onError: () => {
              resolve(0);
              document.body.removeChild(tempContainer);
            }
          }
        });
      }
    });
  };
  const tryParseUrl = async () => {
    const v = url.trim();
    if (!v) return;
    let vid: VideoSource | null = null;
    if (isYouTubeUrl(v)) {
      const duration = await getYouTubeDuration(v);

      if (duration > 600000) { // 10 minutes in milliseconds
        toast({
          title: t('studio.critics.videoTooLongErrorTitle'),
          description: t('studio.critics.videoTooLongErrorDesc'),
          variant: "destructive"
        });
        return;
      }

      vid = {
        type: 'youtube',
        url: v,
        duration
      };
    } else if (/^https?:\/\//i.test(v)) {
      try {
        const duration = await getVideoDurationFromUrl(v);

        if (duration > 600000) { // 10 minutes in milliseconds
          toast({
            title: t('studio.critics.videoTooLongErrorTitle'),
            description: t('studio.critics.videoTooLongErrorDesc'),
            variant: "destructive"
          });
          return;
        }

        vid = {
          type: 'upload',
          storagePath: v,
          downloadURL: v,
          duration
        };
      } catch (e) {
        vid = {
          type: 'upload',
          storagePath: v,
          downloadURL: v,
          duration: 0
        };
      }
    } else {
      toast({
        title: t('studio.critics.invalidUrl'),
        variant: "destructive"
      });
      return;
    }
    setSelectedVideo(vid);
    if (selectedFeedback?.id) {
      await updateFeedback(selectedFeedback.id, {
        video: vid
      } as any);
    }
    setUrl('');
  };
  const toggleSelect = (p: PersonaDoc) => {
    const current = {
      ...selectedIds
    };
    const wasSelected = !!current[p.id];

    // Only allow modifications in draft
    if (selectedFeedback?.status !== 'draft') return;
    if (wasSelected) {
      delete current[p.id];
    } else {
      current[p.id] = true;
    }

    // Maintain local order: always add to the left
    let order = [...selectedOrderIds];
    if (wasSelected) {
      order = order.filter(id => id !== p.id);
    } else {
      order = [p.id, ...order.filter(id => id !== p.id)];
    }
    const remaining = Object.keys(current).filter(id => current[id] && !order.includes(id));
    order = [...order, ...remaining];
    setSelectedIds(current);
    setSelectedOrderIds(order);
    const selectedPersonas = order.map(id => {
      const per = personas.find(per => per.id === id)!;
      return {
        id: per.id,
        name: per.name,
        age: per.age
      };
    });
    if (selectedFeedback?.id) {
      updateFeedback(selectedFeedback.id, {
        selectedPersonas
      } as any);
    }
  };
  const clearVideo = async () => {
    setSelectedVideo(null);
    setUrl('');
    if (selectedFeedback?.id) {
      await updateFeedback(selectedFeedback.id, {
        video: null
      } as any);
    }
  };
  const formatIncome = (income?: number) => {
    if (!income) return t('studio.critics.notSpecified');
    return t('studio.critics.estimatedIncomeValue', {
      value: income.toLocaleString()
    });
  };
  const formatDuration = (durationMs: number): string => {
    const seconds = Math.floor(durationMs / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <ResizablePanelGroup direction="horizontal" className="w-full h-full">
      {/* Left panel - Feedback List */}
      <ResizablePanel defaultSize={24} minSize={16} className="overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="flex-1 overflow-hidden mx-0 py-[5px] px-[10px]">
            <FeedbackList uid={uid} onSelect={setSelectedFeedback} />
          </div>
        </div>
      </ResizablePanel>

      <ResizableHandle />

      {/* Middle panel - Video and Critics */}
      <ResizablePanel defaultSize={52} minSize={20} className="overflow-hidden">
        <ResizablePanelGroup direction="vertical" className="w-full h-full">
          {/* Video section */}
          <ResizablePanel defaultSize={40} maxSize={50} minSize={20} className="overflow-hidden">
            <div className="h-full w-full relative">
              {selectedVideo ? <div className="h-full w-full relative bg-black">
                  {selectedFeedback?.status === 'draft' && <div className="absolute top-2 right-2 z-10">
                      <Button size="icon" variant="secondary" onClick={clearVideo} aria-label={t('studio.critics.removeVideoAria')}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>}
                  {selectedVideo.duration && selectedVideo.duration > 0 && <div className="absolute top-2 left-2 z-10 bg-black/70 text-white px-2 py-1 rounded text-sm font-mono">
                      {formatDuration(selectedVideo.duration)}
                    </div>}
                  {selectedVideo.type === 'youtube' && selectedFeedback?.status !== 'done' ? <iframe title={t('studio.critics.youtubePreviewTitle')} className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${selectedVideo.url.split('v=')[1]?.split('&')[0] || selectedVideo.url.replace('https://youtu.be/', '')}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen /> : <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover bg-black" src={selectedVideo.type === 'upload' ? selectedVideo.downloadURL || selectedVideo.storagePath : selectedVideo.downloadURL || selectedVideo.url} controls onTimeUpdate={e => {
                const video = e.target as HTMLVideoElement;
                setVideoCurrentTime(video.currentTime);
              }} />}
                  {/* Moments bar */}
                  {videoMoments.length > 0 && selectedVideo?.duration && (
                    <div className="absolute bottom-1 left-2 right-2 z-10 flex items-center gap-0.5 px-1">
                      {videoMoments.map((m, i) => {
                        const pct = Math.min(100, Math.max(0, (m.timeMs / selectedVideo.duration) * 100));
                        return (
                          <button
                            key={i}
                            className="text-xs cursor-pointer hover:scale-125 transition-transform"
                            style={{ position: 'absolute', left: `${pct}%`, transform: 'translateX(-50%)' }}
                            onClick={() => seekTo(m.timeMs)}
                            title={`${m.type === 'best' ? 'Best moment' : 'Worst moment'} by ${m.personaName}: ${m.comment.substring(0, 60)}`}
                          >
                            {m.type === 'best' ? '⭐' : '💩'}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div> : <div className={`h-full w-full border border-dashed rounded-lg p-8 text-center transition flex flex-col items-center justify-center ${dragActive ? 'bg-muted/50 border-primary' : 'bg-card border-muted-foreground/25'}`} onDragOver={e => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(true);
            }} onDragLeave={e => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(false);
            }} onDrop={e => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onFile(f);
            }}>
                  <div className="space-y-6 max-w-md mx-auto">
                    <div className="space-y-2">
                      <div className="text-lg font-medium text-foreground">{t('studio.critics.uploadOrPaste')}</div>
                      <div className="text-sm text-muted-foreground">{t('studio.critics.uploadDescription')}</div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex flex-col items-center gap-3">
                        <input type="file" accept="video/*" onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) onFile(f);
                    }} className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90" />
                        <div className="text-xs text-muted-foreground">{t('studio.critics.orDragDrop')}</div>
                      </div>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-muted-foreground/25" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-card px-2 text-muted-foreground">{t('studio.critics.or')}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Input placeholder={t('studio.critics.urlPlaceholder')} value={url} onChange={e => setUrl(e.target.value)} className="flex-1" />
                        <Button type="button" onClick={tryParseUrl} variant="secondary">{t('studio.critics.use')}</Button>
                      </div>
                    </div>
                    
                    {uploading && <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('studio.critics.uploading')}
                      </div>}
                  </div>
                </div>}
            </div>
          </ResizablePanel>

          <ResizableHandle />

          {/* Critics section or Insights section */}
          <ResizablePanel defaultSize={60} minSize={3} className="overflow-hidden">
            <div className="h-full flex flex-col">
              {selectedFeedback?.status === 'done' ?
            // Show Insights when done
            <InsightsList feedbackId={selectedFeedback.id || ''} currentTime={videoCurrentTime} /> :
            // Show Screening Room Catalog for other statuses
            <>
                  <div className="px-4 py-3 border-b">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="text-base font-semibold">{t('studio.critics.title')}</div>
                        <Button size="icon" variant="ghost" onClick={() => setAudienceCatalogHelpOpen(true)} className="h-6 w-6 p-0" aria-label={t('studio.critics.helpTitle')}>
                          <HelpCircle className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="bg-muted text-muted-foreground">{filteredPersonas.length}</Badge>
                        <Button size="sm" variant="ghost" onClick={async () => {
                      // Reset filters first
                      setAgeRange([0, 100]);
                      setSearchText('');
                      setFilterTag('all');
                      // Then refresh catalog
                      await refreshCatalog();
                    }} disabled={loadingPersonas} className="h-8 w-8 p-0">
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3">
                    <div className="grid grid-cols-1 md:grid-cols-[70%_30%] gap-4">
                      <div className="space-y-2">
                        <Input placeholder={t('studio.critics.searchPlaceholder')} value={searchText} onChange={e => {
                      const val = e.target.value;
                      setSearchText(val);
                      if (selectedFeedback?.id) updateFeedback(selectedFeedback.id, {
                        filters: {
                          searchText: val,
                          ageRange
                        }
                      } as any);
                    }} />
                      </div>
                      <div className="space-y-2">
                        <Slider min={0} max={100} step={1} value={ageRange as unknown as number[]} onValueChange={v => {
                      const vr = [v[0], v[1]] as [number, number];
                      setAgeRange(vr);
                      if (selectedFeedback?.id) updateFeedback(selectedFeedback.id, {
                        ageRange: vr,
                        filters: {
                          searchText,
                          ageRange: vr
                        }
                      } as any);
                    }} />
                        <div className="text-xs text-muted-foreground">{t('studio.critics.yearsRange', {
                        min: ageRange[0],
                        max: ageRange[1]
                      })}</div>
                      </div>
                    </div>

                    <div className="mt-3 overflow-x-auto">
                      <div className="flex items-center gap-2 w-max">
                        <Button size="sm" variant={filterTag === 'all' ? 'default' : 'secondary'} className="rounded-full" onClick={() => setFilterTag('all')}>
                          {t('studio.critics.allFilters')}
                        </Button>
                        {filterTags.map(tag => <Button key={tag} size="sm" variant={filterTag === tag ? 'default' : 'secondary'} className="rounded-full" onClick={() => setFilterTag(tag)}>
                            {t('personas.' + tag.toLowerCase().replace(/ /g, '_'))}
                          </Button>)}
                      </div>
                    </div>
                  </div>
                <ScrollArea className="flex-1 pr-2">
                  {loadingPersonas ? <div className="p-4">
                      <div className="flex flex-wrap gap-3">
                        {Array.from({
                      length: 18
                    }).map((_, i) => <Skeleton key={i} className="h-24 w-24 rounded-md" />)}
                      </div>
                    </div> : <div className="divide-y">
                      {filteredPersonas.length === 0 ? <div className="text-sm text-muted-foreground px-4 py-6">{t('studio.critics.noResults')}</div> : filteredPersonas.map(p => {
                    const isSelected = !!selectedIds[p.id];
                    return <div key={p.id} className="p-4 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer" onClick={() => setPreviewPersona(p)}>
                              <div className="flex items-start gap-3">
                                <Avatar className="h-24 w-24 flex-shrink-0">
                                  <AvatarImage src={p.image} alt={p.name} />
                                  <AvatarFallback>{p.name?.[0] || '?'}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0 space-y-1">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-sm truncate">
                                      {p.name}
                                      {p.filter_tag && <span className="ml-1 font-normal text-muted-foreground">{t('studio.critics.inTheStyleOf')}</span>}
                                    </h4>
                                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                      <Checkbox checked={isSelected} disabled={selectedFeedback?.status !== 'draft'} onCheckedChange={() => {
                                if (selectedFeedback?.status !== 'draft') return;
                                toggleSelect(p);
                              }} className="h-6 w-6" aria-label={isSelected ? t('studio.critics.remove') : t('studio.critics.select')} />
                                    </div>
                                  </div>
                                  <div className="text-xs text-muted-foreground space-y-0.5">
                                    <div>{p.age} {t('studio.critics.years')} • {p.gender || t('studio.critics.notAvailable')}</div>
                                    <div>
                                      {p.location?.country || t('studio.critics.unknownCountry')}
                                      {p.location?.city && <span> • {p.location.city}</span>}
                                    </div>
                                    <div className="truncate">{p.occupation || t('studio.critics.occupationUnknown')}</div>
                                    {p.interests && p.interests.length > 0 && <div className="flex flex-wrap gap-1 mt-1">
                                        {p.interests.slice(0, 2).map(interest => <Badge key={interest} variant="secondary" className="text-xs px-1 py-0">
                                            {interest}
                                          </Badge>)}
                                        {p.interests.length > 2 && <Badge variant="outline" className="text-xs px-1 py-0">
                                            +{p.interests.length - 2}
                                          </Badge>}
                                      </div>}
                                      {/* Hidden content to improve search accessibility */}
                                      <span className="sr-only">{JSON.stringify(p)}</span>
                                    </div>
                                </div>
                              </div>
                            </div>;
                  })}
                    </div>}
                </ScrollArea>
                </>}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </ResizablePanel>

      <ResizableHandle />

      {/* Right panel - Selection and Cost */}
      <ResizablePanel defaultSize={35} minSize={30} className="overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <div className="text-base font-semibold flex items-center gap-2">
                {t('studio.critics.audienceTitle')}
                <span className="text-sm text-muted-foreground">({selectedList.length})</span>
              </div>
              {insufficientCredits ? canRequest && <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="rounded-full bg-destructive text-destructive-foreground text-sm px-2.5 py-0.5 font-semibold">
                        {t('studio.critics.creditsPill', {
                      count: Number(totalCost.toFixed(0))
                    })}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('studio.critics.insufficientCreditsTooltip')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider> : canRequest && <span className="rounded-full bg-foreground text-background text-sm px-2.5 py-0.5 font-semibold">
                  {t('studio.critics.creditsPill', {
                count: Number(totalCost.toFixed(0))
              })}
                </span>}
            </div>
          </div>
            <ResizablePanelGroup direction="vertical" className="flex-1">
              <ResizablePanel defaultSize={20} minSize={20} className="overflow-hidden">
                <div className="h-full p-4 flex flex-col gap-4">
                  <div className="overflow-x-auto">
                    {selectedOrdered.length > 0 ? <div className="flex flex-row gap-2 pb-2">
                        {selectedOrdered.map(p => <div key={p.id} className="relative h-14 w-14 rounded-md overflow-hidden bg-muted flex-shrink-0 cursor-pointer" onClick={() => setPreviewPersona(p)}>
                            <img src={(p as any).image || '/placeholder.svg'} alt={p.name} className="h-full w-full object-cover" />
                          </div>)}
                      </div> : <div className="text-sm text-muted-foreground">{t('studio.critics.noAudienceSelected')}</div>}
                  </div>


                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{t('studio.critics.secondEditor.label')}</span>
                      <Badge variant="secondary">BETA</Badge>
                      <Button type="button" size="icon" variant="ghost" onClick={() => setSecondEditorInfoOpen(true)} aria-label="Info">
                        <HelpCircle className="h-4 w-4" />
                      </Button>
                    </div>
                    <Checkbox id="second-editor" checked={secondEditorOpinion} disabled={selectedFeedback?.status !== 'draft'} onCheckedChange={checked => {
                  const val = !!checked;
                  setSecondEditorOpinion(val);
                  if (selectedFeedback?.id) {
                    updateFeedback(selectedFeedback.id, {
                      secondEditorOpinion: val
                    } as any);
                  }
                }} className="h-5 w-5" />
                  </div>

                 

                </div>
              </ResizablePanel>

              <ResizableHandle />

              <ResizablePanel defaultSize={80} minSize={80} className="overflow-hidden">
                <div className="h-full flex flex-col">
                  <div className="p-4 border-b font-medium px-[8px]">
                    {t('studio.critics.screeningNotesTitle')}
                    {selectedFeedback?.status === 'done' && results.length > 0 && <span className="ml-1 text-muted-foreground">({results.length})</span>}
                  </div>
                  
                  {/* Summarized Reports Section */}
                  {selectedFeedback?.status === 'done' && results.length > 0 && <div className="mx-4 mt-4 mb-2">

                        <div className="flex flex-col gap-3">
                          
                            <TooltipProvider>
                              <div className="flex justify-end gap-6 px-[10px] ">
                                
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="outline" size="lg" onClick={() => {
                              if (selectedFeedback?.id) {
                                getFeedbackSummary(selectedFeedback.id).then(setSummaryData);
                              }
                              setRecommendationsOpen(true);
                            }} className="h-[60px] w-[60px]  p-0 bg-background bg-accent/40 hover:bg-accent/90 px-px">
                                      <ClipboardList className="h-10 w-10" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-center">
                                        <div className="font-bold">{t('screeningRoom.recommendations.title')}</div>
                                        <div className="text-sm text-muted-foreground mt-1">{t('screeningRoom.recommendations.description')}</div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                                {selectedOrdered.length > 0 && 
                                
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="outline"  onClick={() => setAudienceModalOpen(true)} className="h-[60px] w-[60px] p-0 bg-background bg-accent/40 hover:bg-accent/90 px-px">
                                        <Users  className="h-10 w-10"/>
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <div className="text-center">
                                          <div className="font-bold">{t('screeningRoom.audience.title')}</div>
                                          <div className="text-sm text-muted-foreground mt-1">{t('screeningRoom.audience.description')}</div>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="outline" size="lg" onClick={() => setAnalyticsModalOpen(true)} className="h-[60px] w-[60px] p-0 bg-background bg-accent/40 hover:bg-accent/90 px-px">
                                        <Heart className="h-10 w-10" />
                                      </Button>
                                   </TooltipTrigger>
                                   <TooltipContent>
                                     <div className="text-center">
                                         <div className="font-bold">{t('screeningRoom.analytics.title')}</div>
                                         <div className="text-sm text-muted-foreground mt-1">{t('screeningRoom.analytics.description')}</div>
                                     </div>
                                   </TooltipContent>
                                 </Tooltip>
                                 {summaryData?.ratings && summaryData.ratings.length > 0 && (
                                   <Tooltip>
                                     <TooltipTrigger asChild>
                                       <Button variant="outline" size="lg" onClick={() => setRatingsModalOpen(true)} className="h-[60px] w-[60px] p-0 bg-background bg-accent/40 hover:bg-accent/90 px-px">
                                         <Star className="h-10 w-10" />
                                       </Button>
                                     </TooltipTrigger>
                                     <TooltipContent>
                                       <div className="text-center">
                                         <div className="font-bold">{t('screeningRoom.ratings.title')}</div>
                                         <div className="text-sm text-muted-foreground mt-1">{t('screeningRoom.ratings.description')}</div>
                                       </div>
                                     </TooltipContent>
                                   </Tooltip>
                                 )}
                              </div>
                            </TooltipProvider>
                        </div>
                      </div>}
                  <div className="flex-1 min-h-0">
                    {selectedFeedback?.status === 'draft' && <div className="h-full flex flex-col items-center justify-center gap-2 p-4 text-center">
                        {!canRequest && <div className="text-xs text-muted-foreground">{t('studio.critics.requirementsHint')}</div>}
                        <Button onClick={() => setScreeningRoomModalOpen(true)} disabled={!canRequest}>
                         {t('studio.critics.startScreening')}
                        </Button>
                      </div>}
                      {selectedFeedback?.status === 'running' && <div className="h-full flex flex-col">
                        <div className="flex items-center justify-center p-8">
                          <img src={runningGifSrc} alt="Processing feedback" className="max-w-[350px] max-h-[350px] w-auto h-auto rounded-lg" />
                        </div>
                        <div className="flex-1">
                          <InsightsList feedbackId={selectedFeedback.id || ''} currentTime={videoCurrentTime} />
                        </div>
                      </div>}
                    {selectedFeedback?.status === 'error' && <div className="h-full flex flex-col items-center justify-center gap-2 text-center p-4">
                        <XCircle className="h-6 w-6 text-destructive" />
                        <div className="text-sm font-medium">{t('studio.critics.errorState')}</div>
                        {(() => {
                          const err = selectedFeedback?.error;
                          if (!err) return null;
                          console.log('[FEEDBACK] rendering error:', { type: typeof err, value: JSON.stringify(err), keys: typeof err === 'object' ? Object.keys(err) : [] });
                          return (
                            <div className="text-xs text-muted-foreground">
                              {typeof err === 'string' ? err : (err?.message || JSON.stringify(err))}
                            </div>
                          );
                        })()}
                      </div>}
                        {selectedFeedback?.status === 'done' && <div className="h-full flex flex-col">
                          <ErrorBoundary name="results-section" showReload>
                           <ScrollArea className="flex-1 pr-2">
                             <div className="p-4 space-y-3">
                             {loadingResults ? <div className="flex items-center justify-center text-sm text-muted-foreground">
                                 <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('studio.critics.loading')}
                                </div> : results.length === 0 ? <div className="text-sm text-muted-foreground">{t('studio.critics.noResults')}</div> : <>
                                  {/* Show personal-feedback results ordered by persona.order */}
                                  {results.filter(r => r.agent === 'personal-feedback').map(r => {
                          const persona = personas.find(p => p.id === r.personaId);
                          return {
                            result: r,
                            persona,
                            order: persona?.order || 999
                          };
                        }).sort((a, b) => a.order - b.order).map(({
                          result,
                          persona
                        }) => <PersonalFeedbackResult key={result.id} result={result} persona={persona} onClick={() => {
                          setSelectedResult(result);
                          setPersonalFeedbackModalOpen(true);
                        }} />)}
                                 
                                 {/* Show fallback if no personal-feedback results */}
                                 {results.filter(r => r.agent === 'personal-feedback').length === 0 && <div className="text-sm text-muted-foreground text-center py-4">
                                     {t('studio.critics.noPersonalFeedbackResults')}
                                   </div>}
                              </>}
                             </div>
                          </ScrollArea>
                          </ErrorBoundary>
                          </div>}
                  </div>
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
        </div>
      </ResizablePanel>

      {/* Second Editor Info Modal */}
      <Dialog open={secondEditorInfoOpen} onOpenChange={setSecondEditorInfoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('studio.critics.secondEditor.infoTitle')}</DialogTitle>
          </DialogHeader>
          <iframe 
            width="100%" 
            height="315" 
            src="https://www.youtube.com/embed/SAIfQuEG0p4?si=xxzTuciwuLFAX40S&amp;controls=0" 
            title="YouTube video player" 
            frameBorder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            referrerPolicy="strict-origin-when-cross-origin" 
            allowFullScreen={false}
          />
          <p className="text-sm text-muted-foreground">{t('studio.critics.secondEditor.infoDesc')}</p>
        </DialogContent>
      </Dialog>

      {/* Audience Catalog Help Modal */}
      <Dialog open={audienceCatalogHelpOpen} onOpenChange={setAudienceCatalogHelpOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('studio.critics.helpTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('studio.critics.helpDescription')}</p>
        </DialogContent>
      </Dialog>


      {/* Persona Preview Modal */}
      <Dialog open={!!previewPersona} onOpenChange={() => setPreviewPersona(null)}>
        <DialogContent className="sm:max-w-3xl rounded-xl border shadow-2xl bg-background">
          {previewPersona && <div className="space-y-6">
              {/* Header with larger square photo */}
              <div className="flex items-start gap-4">
                <div className="h-32 w-32 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                   <img src={previewPersona.image} alt={previewPersona.name} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="flex-1 space-y-2">
                  <h3 className="text-xl font-semibold">
                    {previewPersona.name}
                    {previewPersona.filter_tag && <span className="ml-2 font-normal text-muted-foreground">{t('studio.critics.inTheStyleOf')}</span>}
                  </h3>
                  <div className="text-muted-foreground space-y-1">
                    <div className="flex items-center gap-2">
                      <span>{previewPersona.age} {t('studio.critics.years')}</span>
                      <span>•</span>
                      <span>{previewPersona.gender || t('studio.critics.notAvailable')}</span>
                    </div>
                    {previewPersona.location && <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>
                          {previewPersona.location.country || t('studio.critics.countryNotSpecified')}
                          {previewPersona.location.city && `, ${previewPersona.location.city}`}
                        </span>
                      </div>}
                    {previewPersona.occupation && <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        <span>{previewPersona.occupation}</span>
                      </div>}
                    {previewPersona.estimated_income_eur && <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        <span>{formatIncome(previewPersona.estimated_income_eur)}</span>
                      </div>}
                  </div>
                </div>
              </div>

              {/* Description */}
              {previewPersona.description && <div className="space-y-2">
                  <h4 className="font-semibold">{t('studio.critics.description')}</h4>
                  <p className="text-muted-foreground leading-relaxed">{previewPersona.description}</p>
                </div>}

              {/* Professional info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-semibold">{t('studio.critics.personalInfo')}</h4>
                  <div className="space-y-1 text-sm">
                    {previewPersona.primary_language && <div><span className="font-medium">{t('studio.critics.language')}:</span> {previewPersona.primary_language}</div>}
                    {previewPersona.technological_proficiency && <div><span className="font-medium">{t('studio.critics.techLevel')}:</span> {previewPersona.technological_proficiency}</div>}
                    {previewPersona.location?.environment && <div><span className="font-medium">{t('studio.critics.environment')}:</span> {previewPersona.location.environment}</div>}
                  </div>
                </div>
              </div>

              {/* Interests */}
              {previewPersona.interests && previewPersona.interests.length > 0 && <div className="space-y-2">
                  <h4 className="font-semibold">{t('studio.critics.interests')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {previewPersona.interests.map(interest => <Badge key={interest} variant="secondary">
                        {interest}
                      </Badge>)}
                  </div>
                </div>}

              {/* Subculture */}
              {previewPersona.subculture && previewPersona.subculture.length > 0 && <div className="space-y-2">
                  <h4 className="font-semibold">{t('studio.critics.subculture')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {previewPersona.subculture.map(sub => <Badge key={sub} variant="outline">
                        {sub}
                      </Badge>)}
                  </div>
                </div>}

              {/* Preferred platforms */}
              {previewPersona.preferred_platforms && previewPersona.preferred_platforms.length > 0 && <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    {t('studio.critics.preferredPlatforms')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {previewPersona.preferred_platforms.map(platform => <Badge key={platform} variant="outline">
                        {platform}
                      </Badge>)}
                  </div>
                </div>}

              {/* Actions */}
               <div className="flex justify-end gap-2 pt-4 border-t">
                 <Button variant="outline" onClick={() => setPreviewPersona(null)}>
                   {t('studio.critics.close')}
                 </Button>
                  <Button variant={selectedIds[previewPersona.id] ? 'destructive' : 'default'} disabled={selectedFeedback?.status !== 'draft'} onClick={() => {
              if (!previewPersona) return;
              if (selectedFeedback?.status !== 'draft') return;
              toggleSelect(previewPersona);
              setPreviewPersona(null);
            }}>
                    {selectedIds[previewPersona.id] ? t('studio.critics.removeFromAudience') : t('studio.critics.addToAudience')}
                  </Button>
               </div>
            </div>}
        </DialogContent>
      </Dialog>
      {/* Old Confirm Request Modal - Removed, functionality moved to ScreeningRoomEventModal */}

      {/* Personal Feedback Modal */}
      <PersonalFeedbackModal open={personalFeedbackModalOpen} onOpenChange={setPersonalFeedbackModalOpen} result={selectedResult} persona={selectedResult ? personas.find(p => p.id === selectedResult.personaId) : undefined} />

      {/* Screening Room Event Modal */}
      <ScreeningRoomEventModal open={screeningRoomModalOpen} onOpenChange={setScreeningRoomModalOpen} selectedPersonas={selectedOrdered} initialProfessionalFeedback={secondEditorOpinion} initialNotes={notes} onUpdate={data => {
      // Update local state immediately for real-time sync
      setSecondEditorOpinion(data.professionalFeedback);
      setNotes(data.notes);
      // Update database immediately
      if (selectedFeedback?.id) {
        updateFeedback(selectedFeedback.id, {
          notes: data.notes,
          secondEditorOpinion: data.professionalFeedback
        } as any);
      }
    }} onSubmit={async data => {
      // Execute the screening logic directly (same as old confirm modal)
      if (!selectedFeedback?.id) return;

      // Update final state
      setNotes(data.notes);
      setSecondEditorOpinion(data.professionalFeedback);

      // Update feedback status to running with final data
      await updateFeedback(selectedFeedback.id, {
        status: 'running',
        notes: data.notes,
        secondEditorOpinion: data.professionalFeedback,
        language: data.language,
        runningTimestamp: serverTimestamp() as any
      } as any);

      // Create/update execution record under the user
      if (uid) {
        await setDoc(doc(db, `users/${uid}/executions/${selectedFeedback.id}`), {
          feedbackId: selectedFeedback.id,
          estimatedCost: totalCost,
          type: 'feedback',
          createdAt: serverTimestamp()
        }, {
          merge: true
        });
      }

      // Close modal and update UI
      setScreeningRoomModalOpen(false);
      setSelectedFeedback(prev => prev ? {
        ...prev,
        status: 'running',
        notes: data.notes,
        secondEditorOpinion: data.professionalFeedback,
        language: data.language
      } as any : prev);

      // Reload to reflect changes
      window.location.reload();
    }} videoDuration={selectedVideo?.duration || 0} totalCost={totalCost} canRequest={canRequest} insufficientCredits={insufficientCredits} />

      {/* Results Analytics Modal */}
        <ResultsAnalyticsModal open={resultsAnalyticsOpen} onClose={() => setResultsAnalyticsOpen(false)} results={results} personas={selectedOrdered} />

      {/* Recommendations Modal */}
      <RecommendationsModal open={recommendationsOpen} onClose={() => setRecommendationsOpen(false)} recommendations={summaryData?.recommendations || []} />

      {/* Audience Modal */}
      <AudienceModal selectedPersonas={selectedOrdered} open={audienceModalOpen} onClose={() => setAudienceModalOpen(false)} personas={selectedOrdered} />

      {/* Analytics Modal */}
      <AnalyticsModal open={analyticsModalOpen} onClose={() => setAnalyticsModalOpen(false)} results={results} summary={summaryData} />

      {/* Ratings Modal */}
      <RatingsModal 
        open={ratingsModalOpen} 
        onClose={() => setRatingsModalOpen(false)} 
        ratings={summaryData?.ratings || []} 
        personalFeedbackResults={results.filter(r => r.agent === 'personal-feedback')} 
      />
    </ResizablePanelGroup>;
}